export type GameKey = 'lottery' | 'prediction' | 'trivia' | 'roulette' | 'dance_off' | 'awards';

export interface GameDirections {
  title: string;
  tagline: string;
  djSetup: string[];
  attendeeExperience: string[];
  tips: string[];
}

export const GAME_DIRECTIONS: Record<GameKey, GameDirections> = {
  lottery: {
    title: 'Lottery',
    tagline: 'Random prize delivery to active participants',
    djSetup: [
      'Choose a prize type and configure the lottery mode (one-time or recurring on a timer).',
      'Toggle the lottery on to start. In recurring mode, a winner is drawn automatically at your configured interval.',
      'In one-time mode, tap Draw Winner whenever you\'re ready.',
      'Winners receive a prize notification on their device.',
    ],
    attendeeExperience: [
      'No action required — attendees are automatically eligible if they\'ve submitted a request or vote.',
      'If selected, they see a prize notification on their attendee screen.',
    ],
    tips: [
      'Use recurring mode to keep energy up throughout the night without manual intervention.',
      'Disable \'Allow repeat winners\' so more attendees get a chance.',
    ],
  },

  prediction: {
    title: 'Prediction Game',
    tagline: 'Attendees guess what song plays next — bracket elimination',
    djSetup: [
      'Configure prize type, then tap Start. A new round begins immediately.',
      'Attendees see a join prompt on their screen. They pick which song they think you\'ll play next.',
      'When you start a new song, the round resolves — correct guessers advance, wrong guessers are eliminated.',
      'Repeat across multiple rounds until one player remains. They win the prize.',
    ],
    attendeeExperience: [
      'When a prediction round is active, a pop-up appears asking them to guess the next song from the current queue.',
      'If they guess correctly, they survive the round. Wrong answer = eliminated.',
      'Last player standing wins the prize.',
    ],
    tips: [
      'Works best when the queue has at least 4–5 songs so the choices are meaningful.',
      'Start a new round right after marking a song as Now Playing to keep momentum.',
    ],
  },

  trivia: {
    title: 'Music Trivia',
    tagline: 'AI-generated clues about songs that have played — three rule sets',
    djSetup: [
      'Enable trivia curation from event settings. As songs finish playing, you\'ll be prompted to lock in a trivia clue for each one.',
      'For each song, the AI suggests 3 clue options. Pick one, write your own, or skip the song.',
      'Once you have 4+ locked clues, tap the trivia button to configure and start a game.',
      'Choose a rule set: First Correct Answer (one question, fastest wins), Most Correct Answers (full quiz, highest score wins), or Sudden Death (one wrong = eliminated).',
    ],
    attendeeExperience: [
      'When a game starts, attendees see a join prompt. They tap Join to enter.',
      'Each question shows a trivia clue. They pick the matching song from 4 multiple-choice options.',
      'In Sudden Death: wrong answer = immediately eliminated. Last one standing wins.',
      'In Most Correct: all questions play out, winner is the highest scorer (time breaks ties).',
      'In First Correct: first person to answer the question correctly wins.',
    ],
    tips: [
      'Sudden Death works best as an end-of-night event with 6–10 curated clues.',
      'Use the Verify button on AI-generated clues before locking — it fact-checks the claim.',
      'You can write your own clues for songs you know well — custom clues are often the most fun.',
    ],
  },

  roulette: {
    title: 'Request Roulette',
    tagline: 'A random queued song is spotlighted — one of its fans wins a prize',
    djSetup: [
      'Choose a prize and a mode: one-time, or recurring every few minutes.',
      'Tap Spin Now for a single draw, or Start to draw automatically on your interval.',
      'Each draw spotlights a random queued song that has voters. One of its upvoters or its requester wins the prize.',
      'Optionally, turn on "Play the spotlighted song next" to move the song to the top of your queue.',
    ],
    attendeeExperience: [
      'Every phone shows a banner with the spotlighted song, how many people were in the draw, and the winner.',
      'Attendees who voted for or requested that song see "You were in the draw".',
      'The winner gets a prize notification on their screen.',
    ],
    tips: [
      'Turn off \'Allow repeat winners\' so more attendees get a chance.',
      'Recurring draws need this manage page open in a browser tab.',
    ],
  },

  dance_off: {
    title: 'Dance-off',
    tagline: 'The crowd votes for numbered dancers — the winner claims a prize at the DJ table',
    djSetup: [
      'Set the number of contestants and choose a prize, then tap Start Dance-off.',
      'Every phone gets a pop-up with numbered buttons. Watch the live vote counts on your screen.',
      'Tap Close Voting when the dancing stops. If there\'s a tie, pick randomly, pick yourself, or let everyone tied win.',
      'Show the QR code on your screen to the winner. The first person to scan it gets the prize on their phone.',
    ],
    attendeeExperience: [
      'A pop-up shows big numbered buttons. Attendees tap the number of their favorite dancer.',
      'They can change their vote until you close voting. Vote counts stay hidden from attendees.',
      'When voting closes, every phone shows which contestant won.',
    ],
    tips: [
      'Number the dancers out loud before you start so the crowd knows who is who.',
      'Close voting only when the dancing stops — late votes are refused.',
      'Keep the QR code on screen until it shows as claimed.',
    ],
  },

  awards: {
    title: 'Event Awards',
    tagline: 'Yearbook-style superlatives to close the night',
    djSetup: [
      'Pick which award categories to hand out tonight. Add your own custom awards too.',
      'Optionally attach a prize to any award. It is sent to the winner the moment that award is revealed.',
      'Tap Work out winners. You see each winner and why they won, and you can change any winner.',
      'Optionally show the award names on phones early to build anticipation. Winners stay secret.',
      'Tap Start the awards, then Reveal next for each award, one at a time.',
    ],
    attendeeExperience: [
      'Phones get a push when the awards start.',
      'Each award pops up on every phone as you reveal it. The winner sees "That\'s you!".',
      'Every award stays on the event page, and on the ended page after the event.',
    ],
    tips: [
      'Run the awards before you end the event, while people are still in the room.',
      'Tap Recompute just before you start so late votes count.',
      'A custom award is a good way to recognise someone the data can\'t see.',
    ],
  },
};
